<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'deletedRecordsHome' }"
          marginBottom="5"/>

      <h6 class="element-header">  {{ $t('office.title') }}</h6>

      <div class="element-box">
        <h5 class="form-header mb-4">
          {{ $t('office.deleted_office_list') }}
        </h5>

        <div class="row align-items-center justify-content-between">
          <PerPage :values="[10, 25, 50, 100]" @update:perPage="getOffices"/>

          <div class="form-group mb-0 col-12 col-md-4 mt-3 mt-md-0">
            <input name="searchTerm"
                   class="form-control"
                   @input="handleDebounceSearch"
                   v-model="filterDebounce.searchTerm"
                   :placeholder="$t('general.button.search')"/>
          </div>
        </div>

        <EmptyState
            v-if="!offices.data?.length"
            icon="fa-trash-can"
            title="Keine gelöschten Einträge gefunden"
            subtitle="Es wurden keine gelöschten Einträge gefunden, die Ihren Suchkriterien entsprechen."/>

        <div v-else class="table-responsive overflow-auto pt-4">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>
                <span>{{ $t('general.office_name') }}</span>
              </th>
              <th>
                <span>{{ $t('general.street') }}</span>
              </th>
              <th>
                <span>{{ $t('general.postal_code') }}</span>
              </th>
              <th>
                {{ $t('general.city') }}
              </th>
              <th>
                {{ $t('general.office_engagement_type') }}
              </th>
              <th>
                {{ $t('general.federal_state') }}
              </th>
              <th>
                {{ $t('general.region') }}
              </th>
              <th style=" text-align: right;">
                <a href="#">{{ $t('general.actions') }}</a>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(office) in offices.data" :key="office.id">
              <td>
                {{ office?.name }}
              </td>
              <td>
                {{ office?.address }}
              </td>
              <td>
                {{ office?.zip }}
              </td>
              <td>
                {{ office?.city }}
              </td>
              <td>
                {{ office?.engagementTypeTranslation }}
              </td>
              <td>
                {{ office?.federalStateTranslation }}
              </td>
              <td>
                {{ office?.regionTranslation }}
              </td>
              <td class="text-right">
                <button class="btn btn-primary ml-2"
                        @click="undeleteModal(office?.id, office.name)">
                  <i class="os-icon os-icon-arrow-left7"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <Pagination
            v-if="offices?.pagination?.total_pages > 1"
            :totalPages="offices?.pagination?.total_pages"
            :currentPage="offices?.pagination?.current_page"
            :totalItems="offices?.pagination?.total_items"
            :perPage="offices?.pagination?.items_per_page"
            @pagechanged="getOffices"/>
      </div>
    </div>


    <ModalConfirm
        v-model="showModal"
        :confirmationQuestion=confirmModalMessage
        :header="''"
        :onConfirm="undeleteOffice"/>

  </PageTemplate>
</template>


<script setup>
import deletedRecordsService from '@/api/DeletedRecords';
import CreateButton from '@/components/CreateButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import { CloseToast, ErrorToast, SuccessToast } from '@/components/VueToast';
import debounce from '@popperjs/core/lib/utils/debounce';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BackButton from "@/components/button/BackButton.vue";

const { t }               = useI18n();
const offices             = ref([]);
const confirmModalMessage = ref('');
const filterDebounce      = ref({
  searchTerm: '',
});

const debouncedSearch = debounce(async (query) => {
  await getOffices(1, undefined, filterDebounce.value);
}, 600);

const handleDebounceSearch = (event) => {
  debouncedSearch(event.target.value);
};

const showModal   = ref(false);
const officeId    = ref('');
const isSearching = ref(false);
const isLoaded    = ref(false);

async function getOffices(page = 1, perPage, passedFilters) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }
  const perPageRes = offices.value?.pagination?.items_per_page;

  try {
    const { data } = await deletedRecordsService.getDeletedOfficeList({
      page: page,
      limit: perPage ?? perPageRes,
      ...(passedFilters ? passedFilters : ''),
    });

    offices.value     = data;
    isSearching.value = false;
  } catch (error) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

function undeleteModal(id, name) {
  CloseToast();
  officeId.value            = '';
  confirmModalMessage.value = '';
  officeId.value            = id;
  showModal.value           = true;
  confirmModalMessage.value = 'Willst du ' + name + ' wiederherstellen?';
}

async function undeleteOffice() {
  try {
    await deletedRecordsService.undeleteOffice(officeId.value);

    officeId.value  = false;
    showModal.value = false;

    await getOffices();
    SuccessToast(t('general.toasts.delete_success'));
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(async () => {
  await Promise.all([
    getOffices(),
  ]);

  isLoaded.value = true;
});


</script>

<style scoped>

</style>